// frontend/src/features/inventory/components/ScannableInput.tsx
// ADR-099 Rule LBL-08. کامپوننت مشترک برای اسکن با بارکدخوان فیزیکی (خودکار،
// چون input روی فوکس معمولی کار می‌کند) یا دوربین موبایل/تبلت (toggle صریح کاربر).
//
// وابستگی جدید (باید در package.json اضافه شود، بدون CDN طبق ADR-080):
//   npm install qr-scanner

import { useEffect, useRef, useState } from "react";
import QrScanner from "qr-scanner";

interface ScannableInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onScanned?: (code: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  dir?: "ltr" | "rtl";
  disabled?: boolean;
}

function hasCameraSupport(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    !!navigator.mediaDevices.getUserMedia
  );
}

export function ScannableInput({
  value = "",
  onChange,
  onScanned,
  onFocus,
  onBlur,
  onKeyDown,
  placeholder,
  dir = "ltr",
  disabled = false,
}: ScannableInputProps) {
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scannerRef = useRef<QrScanner | null>(null);

  const showCameraButton = hasCameraSupport();

  const handleScan = (code: string) => {
    onChange?.(code);
    onScanned?.(code);
  };

  useEffect(() => {
    if (!cameraOn || !videoRef.current) return;

    scannerRef.current = new QrScanner(
      videoRef.current,
      (result: QrScanner.ScanResult | string) => {
        if (typeof result === "object" && result !== null && "data" in result) {
          handleScan(result.data);
        } else if (typeof result === "string") {
          handleScan(result);
        }
      },
      {
        preferredCamera: "environment",
        highlightScanRegion: true,
        highlightCodeOutline: true,
        returnDetailedScanResult: true,
        onDecodeError: (_err: Error | string) => {
          // ignore common decode errors during scanning
        },
      }
    );

    scannerRef.current.start().catch((err: Error) => {
      setCameraError("دسترسی به دوربین ممکن نشد. اجازهٔ دوربین را در مرورگر بررسی کنید.");
      setCameraOn(false);
      console.error(err);
    });

    return () => {
      scannerRef.current?.stop();
      scannerRef.current?.destroy();
      scannerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOn]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onKeyDown={(e) => {
            onKeyDown?.(e);
            if (e.defaultPrevented) return;
            if (e.key === "Enter") {
              e.preventDefault();
              handleScan(value);
            }
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          dir={dir}
          disabled={disabled}
          style={{
            flex: 1,
            padding: "0.6rem 0.9rem",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid var(--glass-border)",
            borderRadius: "8px",
            color: "var(--color-text)",
            boxSizing: "border-box",
          }}
        />
        {showCameraButton && (
          <button
            type="button"
            className="btn btn-secondary"
            style={{ padding: "0.5rem 0.7rem" }}
            onClick={() => setCameraOn((prev) => !prev)}
            title={cameraOn ? "خاموش کردن دوربین" : "اسکن با دوربین"}
          >
            {cameraOn ? "📷 روشن" : "📷"}
          </button>
        )}
      </div>

      {cameraOn && (
        <div
          style={{
            border: "1px solid var(--glass-border)",
            borderRadius: "8px",
            overflow: "hidden",
            maxWidth: "320px",
          }}
        >
          <video ref={videoRef} style={{ width: "100%", display: "block" }} />
        </div>
      )}

      {cameraError && (
        <div style={{ color: "red", fontSize: "0.8rem" }}>{cameraError}</div>
      )}
    </div>
  );
}
